import { SearchEmptyState } from "@louise/moon-ui/primitives/SearchEmptyState";
import { useState, useMemo, useCallback } from "react";
import { View, Text, ScrollView, Pressable, Platform } from "react-native";
import { Stethoscope } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { getBlocksByKind, type ContentBlock } from "@src/packages/content";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../onboarding/theme";
import type { ChatStackParamList } from "../../types";
import {
  MoonFilterChip,
  MoonSearchInput,
  BackLink,
  cardSurface,
} from "@louise/moon-ui";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import { useSmartBack, getBackLabel } from "../../hooks/useSmartBack";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { semanticColors, colors, withAlpha } from "@louise/theme/tokens";

// Maps the raw category value coming from CMS to a stable i18n key.
const CATEGORY_I18N_KEYS: Record<string, string> = {
  reproductive: "reproductive",
  endocrine: "endocrine",
  autoimmune: "autoimmune",
  structural: "structural",
  genetic: "genetic",
  "male-factor": "male_factor",
};

type Props = NativeStackScreenProps<ChatStackParamList, "Conditions">;

export default function ConditionsScreen({ route, navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("ConditionsScreen");
  const returnTab = route.params?.returnTab;
  const handleBack = useSmartBack({
    returnTab,
    fallbackTab: "Learn",
    fallbackScreen: "Learn",
  });
  const backLabel =
    returnTab && returnTab !== "Learn"
      ? getBackLabel(returnTab, "Learn", t("you.shared.back"))
      : t("learn.back_to_learn");
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const diagnoses = useMemo(() => getBlocksByKind("condition"), []);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    for (const dx of diagnoses) {
      if (dx.body.category) cats.add(dx.body.category);
    }
    return Array.from(cats).sort();
  }, [diagnoses]);

  const filtered = useMemo(() => {
    let result = diagnoses;

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.body.conditionName.toLowerCase().includes(q) ||
          d.body.description.toLowerCase().includes(q) ||
          d.body.category.toLowerCase().includes(q),
      );
    }

    if (activeCategory) {
      result = result.filter((d) => d.body.category === activeCategory);
    }

    result.sort((a, b) => a.title.localeCompare(b.title));
    return result;
  }, [diagnoses, search, activeCategory]);

  const onItemPress = useCallback(
    (item: ContentBlock<"condition">) => {
      // Condition titles are authored catalogue copy, not patient data.
      capturePress(pressLabel(item.title), "learn_condition_card");
      navigation.navigate("ConditionDetail", { conditionId: item.id });
    },
    [capturePress, navigation],
  );

  const rows = useMemo(() => {
    const result: ContentBlock<"condition">[][] = [];
    for (let i = 0; i < filtered.length; i += 2) {
      result.push(filtered.slice(i, i + 2));
    }
    return result;
  }, [filtered]);

  return (
    <GlassScreenLayout>
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back button */}
        <BackLink
          label={backLabel}
          action="learn_encyclopedia_back"
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 38,
            fontWeight: "700",
            color: colors.deepBrown,
            lineHeight: 42,
            letterSpacing: -0.5,
            ...(Platform.OS === "web" && {
              ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }),
          }}
        >
          {t("learn.encyclopedia_screens.conditions.title")}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            lineHeight: 21,
          }}
        >
          {t("learn.encyclopedia_screens.conditions.subtitle")}
        </Text>

        {/* Search bar */}
        <MoonSearchInput
          value={search}
          onChangeText={setSearch}
          placeholder={t(
            "learn.encyclopedia_screens.conditions.search_placeholder",
          )}
          onClear={() => setSearch("")}
          tone="glass"
          style={{ marginTop: 18 }}
        />

        {/* Category filter chips */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            gap: 8,
            paddingVertical: 14,
          }}
        >
          <MoonFilterChip
            label={t("learn.encyclopedia_screens.all")}
            selected={!activeCategory}
            onPress={() => setActiveCategory(null)}
            tone="glass"
            showCheck={false}
          />
          {categories.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <MoonFilterChip
                key={cat}
                label={
                  CATEGORY_I18N_KEYS[cat]
                    ? t(
                        `learn.encyclopedia_screens.conditions.categories.${CATEGORY_I18N_KEYS[cat]}`,
                      )
                    : cat
                }
                selected={isActive}
                onPress={() => setActiveCategory(isActive ? null : cat)}
                tone="glass"
                showCheck={false}
              />
            );
          })}
        </ScrollView>

        {/* Result count */}
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            color: EMBER.colors.textFaint,
            marginBottom: 14,
          }}
        >
          {t(
            filtered.length === 1
              ? "learn.encyclopedia_screens.conditions.count_one"
              : "learn.encyclopedia_screens.conditions.count_other",
            { params: { count: String(filtered.length) } },
          )}
        </Text>

        {/* 2-column grid */}
        <View style={{ gap: 12 }}>
          {rows.map((row, rowIdx) => (
            <View key={rowIdx} style={{ flexDirection: "row", gap: 12 }}>
              {row.map((dx) => {
                const { body } = dx;
                return (
                  <Pressable
                    key={dx.id}
                    onPress={() => onItemPress(dx)}
                    style={{
                      ...cardSurface(),
                      flex: 1,
                      overflow: "hidden",
                    }}
                  >
                    {/* Icon */}
                    <View
                      style={{
                        alignItems: "center",
                        paddingTop: 24,
                        paddingBottom: 12,
                      }}
                    >
                      <View
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 24,
                          backgroundColor: withAlpha(colors.greyBrown, 0.08),
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Stethoscope
                          size={22}
                          color={colors.greyBrown}
                          strokeWidth={1.5}
                        />
                      </View>
                    </View>

                    {/* Content */}
                    <View
                      style={{
                        padding: 16,
                        paddingTop: 4,
                        gap: 6,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        className="font-dm-sans"
                        style={{
                          fontSize: 15,
                          fontWeight: "700",
                          color: EMBER.colors.text,
                          lineHeight: 20,
                          textAlign: "center",
                        }}
                        numberOfLines={2}
                      >
                        {dx.title}
                      </Text>

                      {/* Category tag */}
                      <View
                        style={{
                          flexDirection: "row",
                          flexWrap: "wrap",
                          justifyContent: "center",
                          gap: 4,
                          marginTop: 4,
                        }}
                      >
                        <View
                          style={{
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 8,
                            backgroundColor: semanticColors.surface.muted,
                            borderWidth: 1,
                            borderColor: semanticColors.border.subtle,
                          }}
                        >
                          <Text
                            className="font-dm-sans"
                            style={{
                              fontSize: 10,
                              fontWeight: "500",
                              color: EMBER.colors.textMid,
                              letterSpacing: 0.2,
                            }}
                          >
                            {CATEGORY_I18N_KEYS[body.category]
                              ? t(
                                  `learn.encyclopedia_screens.conditions.categories.${CATEGORY_I18N_KEYS[body.category]}`,
                                )
                              : body.category}
                          </Text>
                        </View>
                      </View>
                    </View>
                  </Pressable>
                );
              })}
              {row.length === 1 && <View style={{ flex: 1 }} />}
            </View>
          ))}
        </View>

        {/* Empty state */}
        {filtered.length === 0 && (
          <SearchEmptyState
            title={t("learn.encyclopedia_screens.conditions.empty")}
            onReset={() => {
              setSearch("");
              setActiveCategory(null);
            }}
          />
        )}
      </ScrollView>
    </GlassScreenLayout>
  );
}
